import React, { useEffect, useState, useMemo } from 'react';
import { pb } from '@/lib/pocketbase';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { RecordModel } from 'pocketbase';
import { Product as ProductType } from '@/types';

interface ProductRecord extends RecordModel {
  name: string;
  image: string;
  price: number;
  slug: string;
  description: string;
  shortDescription: string;
  cooperative: string;
  moreProductsVisibility: boolean;
  expand?: {
    cooperative: {
      id: string;
      name: string;
      image: string;
      slug: string;
    };
  };
}

interface Product {
  id: string;
  name: string;
  image: string;
  price: number;
  slug: string;
  moreProductsVisibility: boolean;
  cooperative: string;
  expand?: {
    cooperative: {
      id: string;
      name: string;
    };
  };
}

interface MoreProductsProps {
  currentProductId: string;
  cooperativeId: string;
}

// Update the ProductCard component to handle localization
const ProductCard: React.FC<{
  product: ProductType;
  locale: string;
}> = ({ product, locale }) => {
  const imageUrl = useMemo(() => {
    try {
      return pb.files.getURL(product, product.image);
    } catch (error) {
      console.error('Error generating image URL:', error);
      return '/placeholder.jpg';
    }
  }, [product]);

  const getTranslatedContent = (field: 'name') => {
    if (locale === 'en') {
      return product[field];
    }
    return product?.translations?.translations?.np?.[field] || product[field];
  };

  const getFontClass = () => {
    return locale === 'np' ? 'font-noto' : 'font-slate';
  };

  return (
    <Link to={`/product/${product.slug}`}>
      <div className="bg-[rgba(214,255,235,1)] flex flex-col w-[240px] h-[380px] rounded-[11px] overflow-hidden">
        <div className="w-full h-[280px] relative">
          <img
            loading="lazy"
            src={imageUrl}
            className="w-full h-full object-cover"
            alt={product.name}
            onError={(e) => {
              console.error('Image load error:', {
                product: product.id,
                image: product.image
              });
              e.currentTarget.src = '/placeholder.jpg';
            }}
          />
        </div>
        <div className="p-4 flex flex-col justify-between flex-grow">
          <h3 className={`text-2xl font-bold line-clamp-2 ${getFontClass()}`}>
            {getTranslatedContent('name')}
          </h3>
          <p className={`text-lg font-normal ${getFontClass()}`}>
            Rs. {product.price}
          </p>
        </div>
      </div>
    </Link>
  );
};

export const MoreProducts: React.FC<{ currentProductId: string }> = ({ currentProductId }) => {
  const [searchParams] = useSearchParams();
  const locale = searchParams.get('locale') || 'en';
  const [products, setProducts] = useState<ProductType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const currentProduct = await pb.collection('products').getOne<ProductRecord>(currentProductId);
        const cooperativeId = currentProduct.cooperative;

        if (cooperativeId) {
          const records = await pb.collection('products').getList<ProductRecord>(1, 50, {
            filter: `cooperative="${cooperativeId}" && id!="${currentProductId}"`,
            expand: 'cooperative'
          });

          // Transform while preserving PocketBase record properties
          const transformedProducts: ProductType[] = records.items.map(record => ({
            ...record, // Spread all record properties
            id: record.id,
            name: record.name,
            image: record.image,
            price: record.price,
            slug: record.slug,
            description: record.description,
            shortDescription: record.shortDescription,
            cooperative: record.cooperative,
            moreProductsVisibility: record.moreProductsVisibility,
            expand: record.expand
          }));

          setProducts(transformedProducts);
        }
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        setLoading(false);
      }
    };

    if (currentProductId) {
      loadProducts();
    }
  }, [currentProductId]);

  const getFontClass = () => {
    return locale === 'np' ? 'font-noto' : 'font-slate';
  };

  const getSectionTitle = () => {
    const cooperativeName = locale === 'np' 
      ? products[0]?.expand?.cooperative?.nepaliName 
      : products[0]?.expand?.cooperative?.name || 'Sahakari';

    if (locale === 'np') {
      return `${cooperativeName}का अन्य उत्पादनहरू`;
    }
    
    return `More Products from ${cooperativeName}`;
  };

  if (loading || products.length === 0) return null;

  return (
    <section className="bg-[rgba(0,63,40,1)] self-stretch w-full pt-[30px] pb-[30px] px-[19px]">
      <div className="w-full">
        <div className="flex w-full items-center gap-8 justify-between">
          <h2 className={`text-[rgba(255,237,90,1)] text-2xl font-bold self-stretch my-auto ${getFontClass()}`}>
            {getSectionTitle()}
          </h2>
          <button className="bg-[rgba(255,237,90,1)] flex items-center justify-center w-8 h-8 rounded-[14px]">
            <ArrowRight className="w-8 text-[rgba(0,63,40,1)]" />
          </button>
        </div>
        <div className="w-full overflow-x-auto no-scrollbar">
          <div className="flex min-w-max gap-[12px] text-[rgba(0,63,40,1)] mt-[34px] pb-4 py-4">
            {products.map((product) => (
              <ProductCard 
                key={product.id} 
                product={product}
                locale={locale}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
